import type { ListResponse, PullResponse, PushResponse, StoresResponse } from '@familyhub/shared';
import { api, ApiError, isOffline } from '../api/client';
import { categoryOrderKey, db, getMeta, setMeta } from '../db';

/**
 * The sync client (API §3).
 *
 * Pull, then push, then pull again — in that order, because the push may create
 * rows the other phone needs and the second pull is what picks up the server's
 * canonical version of everything just sent.
 *
 * `since` always comes from the previous response's `server_time`, **never from
 * the client clock**. Phone clocks drift and users change timezones; the
 * server's clock is the only ordering authority (API §3.1).
 */

const SINCE_KEY = 'sync_since';
const LAST_SYNC_KEY = 'sync_last_ok';

export type SyncState =
  | 'idle'
  | 'syncing'
  | 'offline'
  /**
   * The session is gone — expired, or revoked from another device. Its own
   * state rather than a general error, because it is the one failure the app
   * can actually resolve: everything else is waited out, this one needs the
   * person to sign in again.
   */
  | 'signed_out'
  | 'error';

type Listener = (state: SyncState) => void;
const listeners = new Set<Listener>();
let current: SyncState = 'idle';

export function onSyncState(listener: Listener): () => void {
  listeners.add(listener);
  listener(current);
  return () => listeners.delete(listener);
}

function setState(state: SyncState) {
  current = state;
  for (const l of listeners) l(state);
}

/* ------------------------------------------------------------------ */
/* Pull                                                                */
/* ------------------------------------------------------------------ */

type Row = Record<string, unknown>;

async function applyChanges(changes: PullResponse['changes']): Promise<void> {
  await db.transaction(
    'rw',
    [db.list_items, db.generic_products, db.product_variants, db.store_category_order],
    async () => {
      // Rows arrive whole, tombstones included. They are stored as sent and
      // filtered at read time — deleting them here would let the other device's
      // next push resurrect them (API §3.1).
      if (changes.list_items.length) {
        await db.list_items.bulkPut(changes.list_items as never[]);
      }
      if (changes.generic_products.length) {
        await db.generic_products.bulkPut(changes.generic_products as never[]);
      }
      if (changes.product_variants.length) {
        await db.product_variants.bulkPut(changes.product_variants as never[]);
      }
      if (changes.store_category_order.length) {
        await db.store_category_order.bulkPut(
          (changes.store_category_order as Row[]).map((r) => ({
            key: categoryOrderKey(String(r.store_id), String(r.category)),
            store_id: String(r.store_id),
            category: String(r.category),
            position: Number(r.position),
            updated_at: String(r.updated_at),
          })),
        );
      }
    },
  );
}

async function pull(): Promise<void> {
  const since = await getMeta<string>(SINCE_KEY);
  let cursor: string | undefined;
  let serverTime: string | undefined;
  let fullResync = false;
  /** Only collected during a full resync, where it decides what is stale. */
  const seen = { list_items: new Set<string>(), generic_products: new Set<string>() };

  // has_more pairs with an opaque cursor passed back verbatim (REVIEW G3).
  for (let page = 0; page < 50; page += 1) {
    const params = new URLSearchParams();
    if (since) params.set('since', since);
    if (cursor) params.set('cursor', cursor);
    const response = await api<PullResponse>(`/api/v1/sync?${params.toString()}`);

    if (response.full_resync) fullResync = true;
    if (fullResync) {
      for (const row of response.changes.list_items) seen.list_items.add(String(row.id));
      for (const row of response.changes.generic_products) {
        seen.generic_products.add(String(row.id));
      }
    }

    await applyChanges(response.changes);
    serverTime = response.server_time;
    if (!response.has_more) break;
    cursor = response.cursor;
    if (!cursor) break;
  }

  // Only once every page is in, or the prune would delete rows that were simply
  // still on their way.
  if (fullResync && serverTime) {
    const removed = await pruneAfterResync(seen);
    // Taking rows off somebody's list without a word is the one thing this is
    // not allowed to do quietly. They were on the screen a moment ago, they are
    // gone now, and the reason is a retention window nobody can be expected to
    // know about. The notice says what happened; Status shows it until dismissed.
    if (removed > 0) {
      await setMeta(RESYNC_NOTICE_KEY, {
        at: new Date().toISOString(),
        removed,
        // The watermark that was too old — the last time this phone was in
        // touch, which is the fact that actually explains the rest.
        last_synced: since ?? null,
      } satisfies ResyncNotice);
    }
  }

  // Advanced only after the whole set has landed: a cursor saved mid-pull would
  // silently skip the pages that never arrived.
  if (serverTime) await setMeta(SINCE_KEY, serverTime);
}

/**
 * Drops local rows the server did not send during a full resync.
 *
 * This phone has been offline longer than the tombstone retention window, so
 * the deletions it missed have been purged server-side and will never arrive
 * (REVIEW G4). Merging would leave things on its list that the household bought
 * three months ago, with nothing to indicate why — the list would simply be
 * wrong, on one phone, forever.
 *
 * Anything still in the outbox is kept regardless. Those are this phone's own
 * unsent changes; the server has never heard of them, so their absence from the
 * payload says nothing at all.
 */
async function pruneAfterResync(seen: {
  list_items: Set<string>;
  generic_products: Set<string>;
}): Promise<number> {
  const pending = new Set<string>();
  for (const entry of await db.outbox.toArray()) {
    const mutation = entry.mutation;
    if (mutation.op === 'upsert' || mutation.op === 'delete') {
      const id = (mutation.row as { id?: string }).id;
      if (id) pending.add(id);
    }
  }

  // Only list items are counted. A generic product disappearing from the
  // catalogue is not something anybody had on a screen; a line off the shopping
  // list is.
  let removedItems = 0;

  await db.transaction('rw', [db.list_items, db.generic_products], async () => {
    for (const row of await db.list_items.toArray()) {
      if (!seen.list_items.has(row.id) && !pending.has(row.id)) {
        // Tombstones were never shown, so their removal is not news.
        if (!row.deleted_at) removedItems += 1;
        await db.list_items.delete(row.id);
      }
    }
    for (const row of await db.generic_products.toArray()) {
      if (!seen.generic_products.has(row.id) && !pending.has(row.id)) {
        await db.generic_products.delete(row.id);
      }
    }
  });

  return removedItems;
}

/**
 * What a full resync took off this phone's list, kept until the person has
 * seen it. Read by the Status screen.
 */
export interface ResyncNotice {
  at: string;
  removed: number;
  last_synced: string | null;
}

export const RESYNC_NOTICE_KEY = 'resync_notice';

export async function resyncNotice(): Promise<ResyncNotice | undefined> {
  return getMeta<ResyncNotice>(RESYNC_NOTICE_KEY);
}

export async function dismissResyncNotice(): Promise<void> {
  await db.meta.delete(RESYNC_NOTICE_KEY);
}

/* ------------------------------------------------------------------ */
/* Push                                                                */
/* ------------------------------------------------------------------ */

/** One batch is capped server-side at 500 mutations. */
const BATCH = 200;

async function push(): Promise<void> {
  for (;;) {
    const batch = await db.outbox.orderBy('seq').limit(BATCH).toArray();
    if (batch.length === 0) return;

    const response = await api<PushResponse>('/api/v1/sync', {
      method: 'POST',
      body: { mutations: batch.map((e) => e.mutation) },
    });

    const applied = new Set(response.applied);
    const rejected = new Map(response.rejected.map((r) => [r.id, r]));

    await db.transaction('rw', [db.outbox, db.rejections], async () => {
      for (const entry of batch) {
        const id = entry.mutation.id;
        if (applied.has(id)) {
          // Already-seen ids come back in `applied` too, which is what lets a
          // retried batch after a flaky supermarket connection clear cleanly.
          await db.outbox.delete(entry.seq!);
        } else if (rejected.has(id)) {
          const r = rejected.get(id)!;
          // A rejection is a validation failure, never a conflict — retrying it
          // forever would wedge the queue, so it is recorded and shown instead.
          const previous = await db.rejections.get(id);
          await db.rejections.put({
            ...r,
            at: new Date().toISOString(),
            // Kept so the app can say what was lost, and retry the one case
            // that resolves itself.
            mutation: entry.mutation,
            attempts: (previous?.attempts ?? 0) + 1,
          });
          await db.outbox.delete(entry.seq!);
        }
        // Anything in neither list stays queued and goes out again next time.
      }
    });

    if (batch.length < BATCH) return;
  }
}

/* ------------------------------------------------------------------ */
/* Stores                                                              */
/* ------------------------------------------------------------------ */

/**
 * Stores are not in the sync set — they are seeded server-side and change only
 * when an adapter's health flips. They are cached here because the list is
 * grouped by store and must render with no connection, and because ADAPTERS
 * §5.3 wants health surfaced **in the app**: the person who needs to know a
 * store's search is down is the one about to go shopping.
 */
async function pullStores(): Promise<void> {
  const response = await api<StoresResponse>('/api/v1/stores');
  await db.stores.bulkPut(
    response.stores.map((s) => ({
      id: s.id,
      code: s.code,
      name: s.name,
      supports_search: s.supports_search,
      health: s.health,
      health_note: s.health_note,
    })),
  );
}

/**
 * Shelf prices are server-derived and not in the sync set (API §3.3), so they
 * arrive with the online list and are cached against their `fetched_at`. That
 * is what lets a price still show in a basement — labelled stale once it is old
 * enough, never presented as current (ADAPTERS §5.2).
 */
async function pullPrices(): Promise<void> {
  const response = await api<ListResponse>('/api/v1/list');

  const rows = response.groups
    .flatMap((g) => g.items)
    .filter((i) => i.variant !== null)
    .map((i) => ({
      variant_id: i.variant!.id,
      price_cents: i.price_cents,
      fetched_at: i.price_fetched_at,
    }));
  if (rows.length) await db.prices.bulkPut(rows);

  /**
   * Suggestions ride along, because they are already in this response.
   *
   * The list screen used to fetch the same endpoint again for them — two
   * identical requests per render — and showed nothing at all offline.
   * Replacing the set wholesale rather than merging is deliberate: a suggestion
   * the server has stopped making should stop being shown, and a stale one
   * lingering is exactly the nagging §7 is about.
   */
  const fetchedAt = new Date().toISOString();
  await db.transaction('rw', [db.suggestions], async () => {
    await db.suggestions.clear();
    if (response.suggestions.length) {
      await db.suggestions.bulkPut(
        response.suggestions.map((s) => ({
          generic_product_id: s.generic_product.id,
          name: s.generic_product.name,
          reason: s.reason,
          confidence: s.confidence,
          suggested_variant_id: s.suggested_variant?.id ?? null,
          fetched_at: fetchedAt,
        })),
      );
    }
  });
}

/**
 * The purchase-history overlay (API §4.1). Server-derived like prices, and
 * cached for the same reason: local search has to rank by history *and* work
 * with no signal, and purchases are not in the sync set.
 */
async function pullHistory(): Promise<void> {
  const response = await api<{ history: HistoryRow[] }>('/api/v1/history');
  await db.history.clear();
  if (response.history.length) await db.history.bulkPut(response.history);
}

interface HistoryRow {
  generic_product_id: string;
  purchase_count: number;
  last_purchased_at: string | null;
  /** The product a re-add defaults to; absent on rows cached before it existed. */
  usual_variant_id?: string | null;
}

/* ------------------------------------------------------------------ */
/* The loop                                                            */
/* ------------------------------------------------------------------ */

let running = false;
let queuedAgain = false;

export async function sync(): Promise<void> {
  if (running) {
    // Coalesce: a burst of edits should produce one more run, not fifteen.
    queuedAgain = true;
    return;
  }
  running = true;
  setState('syncing');

  try {
    // The core: the list itself, and anything queued against it. When this
    // succeeds the household is in sync, whatever else does or does not follow.
    await pull();
    await push();
    // The second pull brings back the server's version of everything just
    // pushed, including rows the other phone created in between.
    await pull();
    await setMeta(LAST_SYNC_KEY, new Date().toISOString());

    /**
     * The overlays, each best-effort.
     *
     * These are extras layered on a list that is already correct — store
     * health, shelf prices, purchase counts. Letting one of them abort the
     * cycle used to mean a phone that had pulled its list perfectly
     * still reported "last synced: never", which is both wrong and alarming: it
     * says the thing in your hand might be days out of date when it is not.
     */
    for (const step of [pullStores, pullPrices, pullHistory]) {
      try {
        await step();
      } catch (err) {
        if (err instanceof ApiError && err.isUnauthenticated) throw err;
        // Not surfaced: the list is right, and an overlay that missed one cycle
        // catches up on the next.
        console.warn(`sync: ${step.name} failed`, err);
      }
    }

    setState('idle');
  } catch (err) {
    if (isOffline(err)) {
      // Being offline is a normal state for this app, not an error. The queue
      // waits; the user is never told to go and find signal.
      setState('offline');
    } else if (err instanceof ApiError && err.isUnauthenticated) {
      // Not rethrown: this is reported through the state so the shell can react
      // once, rather than surfacing as an unhandled rejection from a timer.
      setState('signed_out');
    } else {
      setState('error');
      console.error('sync failed', err);
    }
  } finally {
    running = false;
    if (queuedAgain) {
      queuedAgain = false;
      void sync();
    }
  }
}

/** Milliseconds between background syncs while the app is on screen. */
const POLL_MS = 60_000;

/**
 * Sync on the events that actually mean something changed: coming back online,
 * returning to the app, and a slow poll while it is open. There is no
 * websocket — two people editing one list do not need one, and it would be a
 * connection to keep alive in a supermarket basement.
 */
export function startSyncLoop(): () => void {
  let timer: number | undefined;

  const kick = () => void sync();

  const onVisible = () => {
    if (document.visibilityState === 'visible') kick();
  };

  window.addEventListener('online', kick);
  document.addEventListener('visibilitychange', onVisible);
  timer = window.setInterval(() => {
    if (document.visibilityState === 'visible') kick();
  }, POLL_MS);

  kick();

  return () => {
    window.removeEventListener('online', kick);
    document.removeEventListener('visibilitychange', onVisible);
    if (timer) window.clearInterval(timer);
  };
}

export async function lastSyncedAt(): Promise<string | undefined> {
  return getMeta<string>(LAST_SYNC_KEY);
}
